import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useAccount, useChainId, useWalletClient } from "wagmi";
import { Bridge, BridgeFactory, Erc20 } from "@chainsafe/chainbridge-contracts";
import { ERC721 } from "../Contracts/ERC721.d";
import { ERC1155 } from "../Contracts/ERC1155.d";
import {
  BigNumber,
  BigNumberish,
  ContractTransaction,
  ethers,
  Overrides,
  PayableOverrides,
  utils,
  providers,
} from "ethers";
import { Erc20DetailedFactory } from "../Contracts/Erc20DetailedFactory";
import {
  BridgeConfig,
  chainbridgeConfig,
  TokenConfig,
  TokenConfig1155,
} from "../chainbridgeConfig";
import { transitMessageReducer } from "./Reducers/TransitMessageReducer";
import { Weth } from "../Contracts/Weth";
import { WethFactory } from "../Contracts/WethFactory";
import {
  executeTransactionWithRetry,
  formatBigNumber,
  getGasPriceEstimate,
} from "./chainbridgeHelpers";
import { walletClientToSigner } from "../Utils/wagmiToEthers";
import { usePalmFees } from "./hooks/usePalmFees";
import { useNFTTokens } from "./hooks/useNFTTokens";
import type {
  Vote,
  NFTTokenDeclaration,
  ERC721Metadata,
  DepositParameters,
  TransactionStatus,
} from "./types";

export type {
  Vote,
  NFTTokenDeclaration,
  ERC721Metadata,
  DepositParameters,
  TransactionStatus,
};

interface IChainbridgeContextProps {
  children: React.ReactNode | React.ReactNode[];
}

const resetAllowanceLogicFor = [
  "0xdac17f958d2ee523a2206206994597c13d831ec7", // USDT
];

type AvailableTokenTypes = "ERC20" | "ERC721" | "ERC1155";
type ConfigTypeTuple = [TokenConfig | TokenConfig1155, AvailableTokenTypes];

type ChainbridgeContext = {
  homeChain?: BridgeConfig;
  destinationChain?: BridgeConfig;
  destinationChains: Array<{ chainId: number; name: string }>;
  setDestinationChain(chainId: number): void;
  getTokenType(token: Erc20 | ERC721 | ERC1155): AvailableTokenTypes;
  getAllTokenConfigs(): Array<ConfigTypeTuple>;
  deposit(params: DepositParameters): Promise<void>;
  resetDeposit(): void;
  transactionStatus?: TransactionStatus;
  depositVotes: number;
  relayerThreshold?: number;
  depositNonce?: string;
  inTransitMessages: Array<string | Vote>;
  depositAmount?: number;
  bridgeFee?: number;
  transferTxHash?: string;
  selectedToken?: string;
  wrapToken:
    | ((overrides?: PayableOverrides) => Promise<ContractTransaction>)
    | undefined;
  unwrapToken:
    | ((
        wad: BigNumberish,
        overrides?: Overrides
      ) => Promise<ContractTransaction>)
    | undefined;
  wrapTokenConfig: TokenConfig | undefined;

  // Palm items
  homeChainIsPalm: boolean;
  palmRelayFee: BigNumber;
  palmGreenFee: BigNumber;
  totalPalmFees: BigNumber;
  tollTokenAddress?: string;
  tollToken?: any;
  tollTokenSymbol?: string;
  tollTokenDecimals?: number;
  allowPalmBridgeFees: (gasPrice: number, gasLimit: number) => Promise<void>;

  // NFT bridge items
  nftTokens: Array<NFTTokenDeclaration>;
  getTotalTokensOwnedForNFT: (token: ERC721 | ERC1155) => Promise<number>;
  updateTokenIdsOwnedForNFT: (
    token: ERC721 | ERC1155,
    tokenIds?: Array<string>
  ) => Promise<void>;
  ownedTokenIdsByTokenAddress: Record<string, BigNumber[]>;
  updateMetadataForTokenId: (
    token: ERC721 | ERC1155,
    tokenId: BigNumber | string
  ) => Promise<void>;
  tokenMetadataByTokenAddressAndId: Record<
    string,
    Record<string, ERC721Metadata>
  >;
};

const ChainbridgeContext = React.createContext<ChainbridgeContext | undefined>(
  undefined
);

const ChainbridgeProvider = ({ children }: IChainbridgeContextProps) => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { data: walletClient } = useWalletClient();
  const signer = useMemo(
    () => (walletClient ? walletClientToSigner(walletClient) : undefined),
    [walletClient]
  );

  const [gasPriceEstimate, setGasPriceEstimate] = useState(0);
  const [homeChain, setHomeChain] = useState<BridgeConfig | undefined>();
  const [relayerThreshold, setRelayerThreshold] = useState<number | undefined>(
    undefined
  );
  const [destinationChain, setDestinationChain] = useState<
    BridgeConfig | undefined
  >();
  const [destinationChains, setDestinationChains] = useState<BridgeConfig[]>(
    []
  );
  const [homeBridge, setHomeBridge] = useState<Bridge | undefined>(undefined);
  const [wrapper, setWrapper] = useState<Weth | undefined>(undefined);
  const [wrapTokenConfig, setWrapperConfig] = useState<TokenConfig | undefined>(
    undefined
  );
  const [destinationBridge, setDestinationBridge] = useState<
    Bridge | undefined
  >(undefined);
  const [transactionStatus, setTransactionStatus] = useState<
    TransactionStatus | undefined
  >(undefined);
  const [depositNonce, setDepositNonce] = useState<string | undefined>(
    undefined
  );
  const [depositVotes, setDepositVotes] = useState<number>(0);
  const [inTransitMessages, tokensDispatch] = useReducer(
    transitMessageReducer,
    []
  );
  const [depositAmount, setDepositAmount] = useState<number | undefined>();
  const [bridgeFee, setBridgeFee] = useState<number | undefined>();
  const [transferTxHash, setTransferTxHash] = useState<string>("");
  const [selectedToken, setSelectedToken] = useState<string>("");

  const palmFees = usePalmFees({ signer, homeChain, chainId });
  const nftTokens = useNFTTokens({ signer, homeChain, chainId, address });

  const resetDeposit = () => {
    chainbridgeConfig.chains.length > 2 && setDestinationChain(undefined);
    setTransactionStatus(undefined);
    setDepositNonce(undefined);
    setDepositVotes(0);
    setDepositAmount(undefined);
    tokensDispatch({ type: "resetMessages" });
    setSelectedToken("");
  };

  const handleSetDestination = (chainId: number) => {
    const chain = destinationChains.find((c) => c.chainId === chainId);
    if (!chain) throw new Error("Invalid destination chain selected");
    setDestinationChain(chain);
  };

  // Gas price polling (Ethereum mainnet only)
  useEffect(() => {
    let poller: ReturnType<typeof setInterval>;
    if (chainId === 1) {
      getGasPriceEstimate().then(setGasPriceEstimate);
      poller = setInterval(
        () => getGasPriceEstimate().then(setGasPriceEstimate),
        120 * 1000
      );
    } else {
      setGasPriceEstimate(10);
    }
    return () => {
      if (poller) clearInterval(poller);
    };
  }, [chainId]);

  // Destination bridge provider
  useEffect(() => {
    if (!destinationChain) return;

    let provider: providers.Provider | undefined;
    const { rpcUrl, networkId } = destinationChain;

    if (rpcUrl.startsWith("wss") || rpcUrl.startsWith("ws")) {
      if (rpcUrl.includes("infura")) {
        const key = rpcUrl.split("/").pop()!;
        provider = new ethers.providers.InfuraWebSocketProvider(networkId, key);
      } else if (rpcUrl.includes("alchemyapi")) {
        const key = rpcUrl.split("/").pop()!;
        provider = new ethers.providers.AlchemyWebSocketProvider(
          networkId,
          key
        );
      }
    } else {
      provider = new ethers.providers.JsonRpcProvider(rpcUrl);
    }

    if (provider) {
      setDestinationBridge(
        BridgeFactory.connect(destinationChain.bridgeAddress, provider)
      );
    }
  }, [destinationChain]);

  // Home chain + bridge setup
  useEffect(() => {
    if (chainId && isConnected) {
      const home = chainbridgeConfig.chains.find(
        (c) => c.networkId === chainId
      );
      if (!home) {
        setHomeChain(undefined);
        setHomeBridge(undefined);
        setWrapperConfig(undefined);
        setWrapper(undefined);
        resetDeposit();
        return;
      }
      setHomeChain(home);

      if (!signer) return;

      setHomeBridge(BridgeFactory.connect(home.bridgeAddress, signer));
      setDestinationChains(
        chainbridgeConfig.chains.filter((c) => c.networkId !== chainId)
      );

      if (chainbridgeConfig.chains.length === 2) {
        const destChain = chainbridgeConfig.chains.find(
          (c) => c.networkId !== chainId
        );
        destChain && setDestinationChain(destChain);
      }

      const wrapperToken = home.tokens.find((t) => t.isNativeWrappedToken);
      if (!wrapperToken) {
        setWrapperConfig(undefined);
        setWrapper(undefined);
      } else {
        setWrapperConfig(wrapperToken);
        setWrapper(WethFactory.connect(wrapperToken.address, signer));
      }
    } else {
      setHomeChain(undefined);
      setWrapperConfig(undefined);
      setWrapper(undefined);
    }
    resetDeposit();
  }, [isConnected, chainId, signer]);

  // Relayer threshold + bridge fee
  useEffect(() => {
    if (!homeBridge) return;
    homeBridge
      ._relayerThreshold()
      .then((t) => setRelayerThreshold(BigNumber.from(t).toNumber()));
    homeBridge._fee().then((f) => setBridgeFee(Number(utils.formatEther(f))));
  }, [homeBridge]);

  // Destination bridge event listeners
  useEffect(() => {
    if (!homeChain || !destinationBridge || !depositNonce) return;

    destinationBridge.on(
      destinationBridge.filters.ProposalEvent(
        homeChain.chainId,
        BigNumber.from(depositNonce),
        null,
        null,
        null
      ),
      (originChainId, depositNonce, status, resourceId, dataHash, tx) => {
        switch (BigNumber.from(status).toNumber()) {
          case 1:
            tokensDispatch({
              type: "addMessage",
              payload: `Proposal created on ${destinationChain?.name}`,
            });
            break;
          case 2:
            tokensDispatch({
              type: "addMessage",
              payload: `Proposal has passed. Executing...`,
            });
            break;
          case 3:
            setTransactionStatus("Transfer Completed");
            setTransferTxHash(tx.transactionHash);
            break;
          case 4:
            setTransactionStatus("Transfer Aborted");
            setTransferTxHash(tx.transactionHash);
            break;
        }
      }
    );

    destinationBridge.on(
      destinationBridge.filters.ProposalVote(
        homeChain.chainId,
        BigNumber.from(depositNonce),
        null,
        null
      ),
      async (originChainId, depositNonce, status, resourceId, tx) => {
        const txReceipt = await tx.getTransactionReceipt();
        if (txReceipt.status === 1) setDepositVotes((v) => v + 1);
        tokensDispatch({
          type: "addMessage",
          payload: {
            address: String(txReceipt.from),
            signed: txReceipt.status === 1 ? "Confirmed" : "Rejected",
          },
        });
      }
    );

    return () => {
      //@ts-ignore
      destinationBridge?.removeAllListeners();
    };
  }, [depositNonce, homeChain, destinationBridge, destinationChain]);

  const getAllTokenConfigs = (): Array<ConfigTypeTuple> => {
    if (!homeBridge || !homeChain) return [];
    return [
      ...homeChain.tokens.map<ConfigTypeTuple>((t) => [t, "ERC20"]),
      ...homeChain.erc721Tokens.map<ConfigTypeTuple>((t) => [t, "ERC721"]),
      ...homeChain.erc1155Tokens.map<ConfigTypeTuple>((t) => [t, "ERC1155"]),
    ];
  };

  const getTokenType = (
    token: Erc20 | ERC721 | ERC1155
  ): AvailableTokenTypes => {
    if (!homeBridge || !homeChain)
      throw new Error("Home bridge contract is not instantiated");
    const match = getAllTokenConfigs().find(
      (tuple) => tuple[0].address === token.address
    );
    if (!match)
      throw new Error("Could not find token for address " + token.address);
    return match[1];
  };

  const deposit = async ({
    recipient,
    tokenAddress,
    amount,
    tokenId,
  }: DepositParameters) => {
    if (!homeBridge || !homeChain) {
      console.log("Home bridge contract is not instantiated");
      return;
    }
    if (!destinationChain || !destinationBridge) {
      console.log("Destination bridge contract is not instantiated");
      return;
    }
    if (!address || !signer) {
      console.log("No signer");
      return;
    }

    const allTokensConfigs = getAllTokenConfigs();
    const matchingTokenTuple = allTokensConfigs.find(
      (token) => token[0].address === tokenAddress
    );
    if (!matchingTokenTuple) {
      console.log("Invalid token selected");
      return;
    }

    const token = matchingTokenTuple[0];
    const tokenType = matchingTokenTuple[1];
    const isErc20 = tokenType === "ERC20";
    const isErc721 = tokenType === "ERC721";
    const isErc1155 = tokenType === "ERC1155";

    let data = "";
    const gasPrice = homeChain.defaultGasPrice || gasPriceEstimate;
    const depositGasLimit = isErc20 ? 350000 : 515000;
    const approvalGasLimit = 52000;

    try {
      if (palmFees.homeChainIsPalm) {
        setTransactionStatus("Paying Bridge Fees");
        await palmFees.allowPalmBridgeFees(gasPrice, approvalGasLimit);
      }

      setTransactionStatus("Initializing Transfer");
      setSelectedToken(tokenAddress);

      if (isErc20) {
        if (amount == undefined) return;
        setDepositAmount(amount);

        const erc20 = Erc20DetailedFactory.connect(tokenAddress, signer);
        const erc20Decimals = await erc20.decimals();

        data =
          "0x" +
          utils
            .hexZeroPad(
              formatBigNumber(amount || 0, erc20Decimals).toHexString(),
              32
            )
            .substr(2) +
          utils
            .hexZeroPad(utils.hexlify((recipient.length - 2) / 2), 32)
            .substr(2) +
          recipient.substr(2);

        const currentAllowance = await erc20.allowance(
          address,
          homeChain.erc20HandlerAddress
        );

        if (
          Number(utils.formatUnits(currentAllowance, erc20Decimals)) < amount
        ) {
          if (
            Number(utils.formatUnits(currentAllowance, erc20Decimals)) > 0 &&
            resetAllowanceLogicFor.includes(tokenAddress)
          ) {
            const resetAllowanceTx = (options: Overrides) =>
              erc20.approve(
                homeChain.erc20HandlerAddress,
                formatBigNumber("0", erc20Decimals),
                options
              );
            await (
              await executeTransactionWithRetry(
                { gasPrice, gasLimit: approvalGasLimit },
                resetAllowanceTx
              )
            ).wait(1);
          }

          const approveAmountTx = (options: Overrides) =>
            erc20.approve(
              homeChain.erc20HandlerAddress,
              formatBigNumber(amount, erc20Decimals),
              options
            );
          await (
            await executeTransactionWithRetry(
              { gasPrice, gasLimit: approvalGasLimit },
              approveAmountTx
            )
          ).wait(1);
        }
      } else if (isErc721) {
        const { ERC721__factory: ERC721Factory } = await import(
          "../Contracts/factories/ERC721__factory"
        );
        const id = BigNumber.from(tokenId);
        const erc721 = ERC721Factory.connect(tokenAddress, signer);
        data =
          "0x" +
          ethers.utils.hexZeroPad(ethers.utils.hexlify(id), 32).substr(2) +
          ethers.utils
            .hexZeroPad(ethers.utils.hexlify((recipient.length - 2) / 2), 32)
            .substr(2) +
          ethers.utils.hexlify(recipient).substr(2);

        const approved = await erc721.getApproved(String(id));
        if (approved !== homeChain.erc721HandlerAddress) {
          const approve721TransferTx = (options: Overrides) =>
            erc721.approve(homeChain.erc721HandlerAddress, String(id), options);
          await (
            await executeTransactionWithRetry(
              { gasPrice, gasLimit: approvalGasLimit },
              approve721TransferTx
            )
          ).wait(1);
        }
      } else if (isErc1155) {
        const { ERC1155__factory: ERC1155Factory } = await import(
          "../Contracts/factories/ERC1155__factory"
        );
        const id = BigNumber.from(tokenId);
        const erc1155 = ERC1155Factory.connect(tokenAddress, signer);
        const erc1155Amount = 1;
        const extradata = "0x";
        data =
          "0x" +
          ethers.utils.hexZeroPad(ethers.utils.hexlify(id), 32).substr(2) +
          ethers.utils
            .hexZeroPad(ethers.utils.hexlify(erc1155Amount), 32)
            .substr(2) +
          ethers.utils
            .hexZeroPad(ethers.utils.hexlify((recipient.length - 2) / 2), 32)
            .substr(2) +
          ethers.utils
            .hexZeroPad(ethers.utils.hexlify((extradata.length - 2) / 2), 32)
            .substr(2) +
          ethers.utils.hexlify(recipient).substr(2) +
          ethers.utils.hexlify(extradata).substr(2);

        const approved = await erc1155.isApprovedForAll(
          address,
          homeChain.erc1155HandlerAddress
        );
        if (approved !== true) {
          const approve1155TransferTx = (options: Overrides) =>
            erc1155.setApprovalForAll(
              homeChain.erc1155HandlerAddress,
              true,
              options
            );
          await (
            await executeTransactionWithRetry(
              { gasPrice, gasLimit: approvalGasLimit },
              approve1155TransferTx
            )
          ).wait(1);
        }
      }

      homeBridge.once(
        homeBridge.filters.Deposit(
          destinationChain.chainId,
          token.resourceId,
          null
        ),
        (destChainId, resourceId, depositNonce) => {
          setDepositNonce(`${depositNonce.toString()}`);
          setTransactionStatus("In Transit");
        }
      );

      const depositTx = (options: Overrides) => {
        const value = formatBigNumber(bridgeFee || 0, 18);
        return homeBridge.deposit(
          destinationChain.chainId,
          token.resourceId,
          data,
          { ...options, value }
        );
      };
      await (
        await executeTransactionWithRetry(
          { gasPrice, gasLimit: depositGasLimit },
          depositTx
        )
      ).wait(1);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      setTransactionStatus("Transfer Aborted");
      return Promise.reject();
    }
  };

  return (
    <ChainbridgeContext.Provider
      value={{
        homeChain,
        destinationChain,
        destinationChains: destinationChains.map((c) => ({
          chainId: c.chainId,
          name: c.name,
        })),
        setDestinationChain: handleSetDestination,
        getTokenType,
        getAllTokenConfigs,
        deposit,
        resetDeposit,
        depositVotes,
        relayerThreshold,
        depositNonce,
        bridgeFee,
        transactionStatus,
        inTransitMessages,
        depositAmount,
        transferTxHash,
        selectedToken,
        wrapToken: wrapper?.deposit,
        wrapTokenConfig,
        unwrapToken: wrapper?.withdraw,

        ...palmFees,
        nftTokens: nftTokens.nftTokens,
        getTotalTokensOwnedForNFT: nftTokens.getTotalTokensOwnedForNFT,
        updateTokenIdsOwnedForNFT: nftTokens.updateTokenIdsOwnedForNFT,
        ownedTokenIdsByTokenAddress: nftTokens.ownedTokenIdsByTokenAddress,
        updateMetadataForTokenId: nftTokens.updateMetadataForTokenId,
        tokenMetadataByTokenAddressAndId:
          nftTokens.tokenMetadataByTokenAddressAndId,
      }}
    >
      {children}
    </ChainbridgeContext.Provider>
  );
};

const useChainbridge = () => {
  const context = useContext(ChainbridgeContext);
  if (context === undefined) {
    throw new Error(
      "useChainbridge must be called within a ChainbridgeProvider"
    );
  }
  return context;
};

export { ChainbridgeProvider, useChainbridge };
